import { debug, error } from '../../../utils/logger'
import { type EmbedElementContext } from '../../element-embedder'
import { DefaultIFrameEmbedComponent, type EmbedIFrameComponent } from '../../embed-component'
import { type EmbedTarget } from '../../embed-targets/embed-target'
import { type MediaControls } from '../../media'
import { type WithIFrame, type EmbedStrategy } from '../embed-strategy'
import { debounce } from 'lodash-es'
export class IFrameEmbedStrategy implements EmbedStrategy, WithIFrame {
  embedTarget: EmbedTarget
  mediaControl: MediaControls | undefined
  ctx: EmbedElementContext
  #iframeObserver: ResizeObserver | undefined
  iframeEmbedHandler: IFrameEmbedHandler
  /**
   * embedded component.
   * keeps information of embedded experience such as iframe element, root element, etc.
   */
  iFrameComponent: EmbedIFrameComponent

  constructor (embedTarget: EmbedTarget, iframeEmbedHandler: IFrameEmbedHandler) {
    this.embedTarget = embedTarget
    this.mediaControl = embedTarget.mediaControl
    this.iframeEmbedHandler = iframeEmbedHandler
    this.ctx = embedTarget.ctx
    this.iFrameComponent = new DefaultIFrameEmbedComponent(this.ctx.url, this.ctx)
  }

  async embed (): Promise<void> {
    this.iFrameComponent.iframeEl.setAttribute('allowFullScreen', '')
    await this.iframeEmbedHandler.initIFrame(this.iFrameComponent)
    this.embedTarget.setPlaysInline(true)
    this.setupMediaControl()
    await this.setupTeleport()
  }

  protected async setupTeleport () {
    if ((this.ctx.settings.teleport == null) || this.ctx.settings.teleport?.disabled === true) return
    // wait for playerjs init
    const observer = new ResizeObserver(debounce(() => {
      this.onTeleportUpdate()
    }, this.ctx.settings.teleport?.resizeDebounceMs ?? 200))
    observer.observe(this.iFrameComponent.rootEl)
    this.#iframeObserver = observer

    this.ctx.eventEmitter.on('playerjs.ready', () => {
      // initialize once after playerjs is readied
      this.onTeleportUpdate()
    })
  }

  #computeIsTeleportActive () {
    if (this.ctx.settings.teleport?.forced === true) return true
    const width = this.ctx.targetEl?.offsetWidth ?? 0
    if ((this.ctx.settings.teleport == null) || this.ctx.settings.teleport.disabled === true) return false
    if (this.ctx.settings.teleport.query == null) return true
    const query = this.ctx.settings.teleport.query
    if (query.minWidth != null && width < query.minWidth) return false
    if (query.maxWidth != null && width > query.maxWidth) return false
    return true
  }

  protected onTeleportUpdate () {
    const value: boolean = this.#computeIsTeleportActive()
    if (this.ctx.teleportActive === value) return
    this.ctx.teleportActive = value
    debug(`teleport changed: ${String(value)}`)
    this.iframeEmbedHandler.onTeleportUpdate().catch(error)
    if (this.ctx.playerjs?.player != null) this.ctx.playerjs.player.send({ method: 'orientation', value: value ? 'portrait' : 'landscape' })
  }

  protected setupMediaControl () {
    if (this.mediaControl == null) return
    this.ctx.eventEmitter.on('playerjs.ready', () => {
      this.ctx.eventEmitter.on('playerjs.click', () => {
        // If teleport is active, iframe is rendered outside the target. Do not toggle play/pause
        if (this.ctx.teleportActive === true) return
        this.mediaControl?.paused === true ? this.mediaControl.play() : this.mediaControl?.pause()
      })
      if (this.ctx.settings.overlayTimeSync === true) {
        this.mediaControl?.on?.('timeupdate', () => {
          (this.mediaControl != null) && this.ctx.playerjs?.player.send({ method: 'setCurrentTime', value: this.mediaControl.currentTime })
        })
      }
    })
  }

  destroy () {
    this.#iframeObserver?.disconnect?.()
    this.iframeEmbedHandler.destroy?.()
  }

  show (): void {
    this.iframeEmbedHandler.show()
  }

  hide (): void {
    this.iframeEmbedHandler.hide()
  }
}

export interface IFrameEmbedHandler {
  initIFrame (iFrameComponent: EmbedIFrameComponent): Promise<void>
  onTeleportUpdate (): Promise<void>
  destroy?(): void
  show (): void
  hide (): void
}
